import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";
import SearchSelect from "./UsersSearchSelect";
import DeleteIcon from "@mui/icons-material/Delete";
import { isEqual } from "lodash";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 1200,
  minHeight: "80vh",
  bgcolor: "#fff",
  borderRadius: "4px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

interface IProps {
  data: any;
  setData: (data: any) => void;
  getRoles: (object?: any) => void;
  handleClose: () => void;
  removeAssignedUsers: () => void;
}

export default function RoleModal({
  data,
  setData,
  getRoles,
  handleClose,
  removeAssignedUsers,
}: IProps) {
  const { user, roles_permissions, permissions, setState } =
    useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);

  const ALL_PERMISSIONS = permissions.reduce((acc: any, curr: any) => {
    acc = [...acc, ...curr.perm];
    return acc;
  }, [] as any[]);

  const submitModal = async () => {
    if (!user.permissions.includes("add_system_role"))
      return toast.error("You dont have permissions to do this action");

    if (!data.data.name.trim()) return toast.error("Name is required");
    if (!data.data.permissions.length) return toast.error("Permissions is required");

    setIsLoading(true);
    try {
      const response = await axios.post("/roles/create", {
        name: data.data.name,
        status: data.data.status ? 1 : 0,
        permissions: data.data.permissions.map((permission: any) => typeof permission === "string" ? permission : permission.name),
      });
      setState({
        roles_permissions: [response.data.data, ...roles_permissions],
      });

      toast.success(response.data.message);
      getRoles();
      handleClose();
    } catch (error) {
      console.log(error);
    }

    setIsLoading(false);
  };

  const editRole = async () => {
    if (!user.permissions.includes("edit_system_role"))
      return toast.error("You dont have permissions to do this action");

    const editRoleData = {
      role_id: data.data.id,
      name: data.data.name,
      status: data.data.status === "active" ? 1 : 0,
      permissions: data.data.permissions.map((permission: any) => typeof permission === "string" ? permission : permission.name),
    };
    try {
      const { data: editRoleResponse } = await axios.post(
        "/roles/update",
        editRoleData
      );

      toast.success(editRoleResponse.message);
      getRoles();
      handleClose();
    } catch (error) {
      console.log(error, "error");
    }
  };

  const deleteAllUsers = async () => {
    if (!user.permissions.includes("delete_system_role_user"))
      return toast.error("You dont have permissions to do this action");

    try {
      await axios.post("/roles/delete-user-from-role", {
        role_id: data.data.id,
      });

      setData({
        users: [],
      });
      removeAssignedUsers();
    } catch (error) {
      console.log(error, "error from delete all users requeset");
    }
  };

  useEffect(() => {
    if (
      isEqual(
        ALL_PERMISSIONS.map((permission: any) => permission.name),
        data.data.permissions.map((permission: any) => permission.name)
      )
    ) {
      return setData({
        roles: ["administrator"],
      });
    }

    if (data.data.roles?.includes("administrator")) return;

    if (!data.data.permissions.length) {
      setData({ roles: [] });
    }
  }, [data.data.permissions]);

  return (
    <Modal
      keepMounted
      open={data.status}
      onClose={handleClose}
      aria-labelledby="Role-Modal"
    >
      <Box sx={style}>
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #d3d3d3",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ wordBreak: "break-word", alignSelf: "center" }}
          >
            {data.edit ? `Edit ${data.data.name} Role` : "Add New Role"}
          </Typography>

          {!data.edit && (
            <FormGroup
              sx={{ maxWidth: "fit-content", justifyContent: "center" }}
            >
              <FormControlLabel
                control={<Switch checked={data.data.status} />}
                label={data.data.status ? "Active" : "Inactive"}
                onChange={(event: any) =>
                  setData({
                    ...data.data,
                    status: event.target.checked ? 1 : 0,
                  })
                }
              />
            </FormGroup>
          )}
        </Box>

        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            overflowY: "auto",
            maxHeight: "60vh",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "normal" }}>
            General Information
          </Typography>
          <Box sx={{ width: "100%" }}>
            <TextField
              fullWidth
              id={`filled-role-name`}
              name="name"
              label="Role Name"
              variant="standard"
              value={data.data.name}
              onChange={(event) => {
                setData({ name: event.target.value });
              }}
              autoFocus={data.data.name !== ""}
            />
          </Box>

          <Divider sx={{ m: "1rem 0" }} />

          {data.edit && (
            <>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "normal",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                Users
                <Tooltip title="Delete all users from role">
                  <IconButton
                    color="error"
                    aria-label="remove all users from role"
                    component="span"
                    onClick={deleteAllUsers}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Box>
                <SearchSelect
                  updateUsers={(user_id: number | string) => {
                    setData({
                      users: data.data.users.filter(
                        (user: any) => user.id !== user_id
                      ),
                    });
                  }}
                  users={data.data.users}
                  role_id={data.data.id}
                />
              </Box>
              <Divider sx={{ m: "1rem 0" }} />
            </>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              Permissions
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                rowGap: 4,
                mt: 3,
              }}
            >
              {permissions.map((role: any) => {
                if (role.name === "administrator") return <></>;

                let checkIfSomePermisioIsFromRole: boolean = false;
                if (
                  role.perm
                    .map((element: any) => {
                      return (
                        data.data.permissions.filter(
                          (permission: any) => permission.name === element.name
                        )[0] || {}
                      );
                    })
                    .some(
                      (element: any) =>
                        "is_custom" in element && !element.is_custom
                    )
                ) {
                  checkIfSomePermisioIsFromRole = true;
                } else {
                  checkIfSomePermisioIsFromRole = false;
                }


                return (
                  <Box
                    key={role.key}
                    sx={{
                      flex: "1 1 300px",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: "4px",
                      pb: 2,
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        mt: -3.5,
                        ml: 1,
                        paddingInlineEnd: "1rem",
                        bgcolor: "#fff",
                      }}
                      label={role.name}
                      control={
                        <Checkbox
                          checked={role.perm.every((element: any) => {
                            return data.data.permissions
                              .map((permission: any) => permission.name)
                              .includes(element.name);
                          })}
                          indeterminate={
                            role.perm.some((r: any) =>
                              data.data.permissions
                                .map((permission: any) => permission.name)
                                .includes(r.name)
                            ) &&
                            !role.perm.every((element: any) => {
                              return data.data.permissions
                                .map((permission: any) => permission.name)
                                .includes(element.name);
                            })
                          }
                          disabled={checkIfSomePermisioIsFromRole}
                          onChange={(e) => {
                            const newPermissions =
                              e.target.checked === true
                                ? [
                                  ...data.data.permissions,
                                  ...(role.perm.map((r: any) => r) || []),
                                ]
                                : data.data.permissions.filter(
                                  (permission: any) =>
                                    !role.perm
                                      .map((r: any) => r.name)
                                      .includes(permission.name) ||
                                    permission.name === role.name
                                );

                            setData({
                              permissions: newPermissions,
                            });
                          }}
                        />
                      }
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        ml: 5,
                        width: "fit-content",
                      }}
                    >
                      {role.perm.map((child: any, index: number) => {
                        const currentPermission =
                          data.data.permissions.find(
                            (permission: any) => permission.name === child.name
                          ) || {};
                        const myPermissionsName = data.data.permissions.map(
                          (permission: any) => permission.name
                        );
                        let selectedPermissionsArray: any = [];
                        role.perm.forEach((per: any) => {
                          myPermissionsName.includes(per.name) &&
                            selectedPermissionsArray.push(per);
                        });
                        const length =
                          role.perm.filter((rr: any) =>
                            rr.name.includes("view")
                          ).length > 1
                            ? 2
                            : 1;
                        const checkChildStartWithView =
                          role.perm.find((r: any) => {
                            return myPermissionsName.includes(r.name);
                          }) &&
                          child.name.includes("view") &&
                          selectedPermissionsArray.length > length;
                        const disabledCheckbox =
                          ("is_custom" in currentPermission &&
                            !currentPermission.is_custom) ||
                          checkChildStartWithView;

                        return (
                          <FormControlLabel
                            key={child.name + " " + index}
                            label={child.name}
                            sx={{
                              width: "fit-content",
                            }}
                            control={
                              <Checkbox
                                checked={
                                  data.data.permissions
                                    .map((permission: any) => permission.name)
                                    .includes(child.name)
                                }
                                disabled={disabledCheckbox}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setData({
                                      permissions: [
                                        ...data.data.permissions,
                                        ...(data.data.permissions.includes(
                                          role.perm.find((rr: any) =>
                                            rr.name.includes("view")
                                          )
                                        )
                                          ? []
                                          : [
                                            ...role.perm.filter((rr: any) =>
                                              rr.name.includes("view")
                                            ),
                                          ]),
                                        child,
                                      ],
                                    });
                                  } else {
                                    setData({
                                      permissions: data.data.permissions.filter(
                                        (permission: any) =>
                                          permission.name !== child.name &&
                                          permission.name !== role.name
                                      ),
                                    });
                                  }
                                }}
                              />
                            }
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #d3d3d3",
            p: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={data.edit ? editRole : submitModal}
            disabled={isLoading}
            endIcon={isLoading && <CircularProgress size={20} />}
          >
            {data.edit ? "Edit" : "Create"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={isLoading}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
